import React from 'react';
import {
  LoadingIndicator,
  IconPlay,
  IconArrowRight,
} from '@spotify-internal/encore-web';
import { sendEvent } from '../Tracking';
import { ContentfulImage } from '../Contentful/ContentfulImage';
import { SearchHit, itemToSearchHit } from '../Search';
import { formatText, fixHref } from '../utils';
import { DEFAULT_SOCIAL_CARD } from '../Layout/PageHead';
import {
  StyledLink,
  Wrapper,
  Headline,
  Subtext,
  CTALink,
  Item,
  ItemList,
  ImageWrapper,
  ItemLabel,
  ItemTitle,
  ItemText,
  LoadMoreButton,
  LoadMoreButtonWrapper,
} from './index.styles';

import { Supertext } from '../ui';
import { ModuleType } from '../Module';

type ContentGridType = {
  trackCategory: string;
  supertext?: string;
  headline?: string;
  subtext?: string;
  ctaLink?: string;
  ctaText?: string;
  items?: any[];
  featuredItems?: number;
  getPageItems?: (page: number) => Promise<any[]>;
  hasNextPage?: (curPage: number) => boolean;
  placeholders?: boolean;
};

export const ContentGrid = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  ContentGridType
>(
  (
    {
      supertext,
      headline,
      subtext,
      ctaText,
      ctaLink,
      trackCategory,
      items: initialItems = [],
      featuredItems = 0,
      getPageItems = () => Promise.resolve([]),
      hasNextPage = () => false,
      placeholders = true,
    },
    ref,
  ) => {
    const [items, setItems] = React.useState<SearchHit[]>([]);
    const page = React.useRef(1);
    const [pageLoading, setPageLoading] = React.useState(false);

    React.useEffect(() => {
      const newItems = initialItems
        .map(itemToSearchHit)
        .filter(Boolean) as NonNullable<SearchHit[]>;
      setItems(newItems);
    }, [initialItems]);

    const loadNextPage = async (e: React.MouseEvent) => {
      e.preventDefault();
      setPageLoading(true);
      sendEvent({
        eventCategory: `Frodor - ${trackCategory}`,
        eventAction: 'click',
        eventLabel: `Load More - Page ${page.current + 1}`,
      });
      const pageItems = await getPageItems(++page.current);
      const newItems = pageItems
        .map(itemToSearchHit)
        .filter(Boolean) as NonNullable<SearchHit[]>;
      setItems([...items, ...newItems]);
      setPageLoading(false);
    };

    return (
      <Wrapper ref={ref as React.RefObject<HTMLDivElement>}>
        {supertext && <Supertext>{supertext}</Supertext>}
        {headline && <Headline>{headline}</Headline>}
        {subtext && (
          <Subtext dangerouslySetInnerHTML={{ __html: formatText(subtext) }} />
        )}
        {ctaText && ctaLink && (
          <CTALink
            href={fixHref(ctaLink)}
            track={[trackCategory, `CTA Link - ${ctaText}`]}
          >
            {ctaText}
            <IconArrowRight iconSize={16} />
          </CTALink>
        )}

        <ItemList>
          {(!items?.length && <LoadingIndicator />) ||
            items.map((item, index) => (
              <Item key={item.id} featured={index < featuredItems}>
                <StyledLink
                  href={item.url}
                  track={[
                    trackCategory,
                    `Content Link - [${item.contentType}]: ${item.title}`,
                  ]}
                  aria-label={`Go to ${item.title}`}
                >
                  <ImageWrapper
                    video={
                      item.contentType === 'video' ||
                      item.contentType === 'videoPage'
                    }
                    featured={index < featuredItems}
                  >
                    {(item.image && (
                      <ContentfulImage
                        asset={item.image}
                        placeholder={placeholders}
                      >
                        <IconPlay />
                      </ContentfulImage>
                    )) || (
                      // placeholder if no image, for now
                      <img
                        data-src={DEFAULT_SOCIAL_CARD}
                        alt={`Spotify for Artists - ${item.contentType}`}
                        className="lazyload"
                      />
                    )}
                  </ImageWrapper>

                  {item.label && <ItemLabel>{item.label}</ItemLabel>}

                  {item.title && <ItemTitle>{item.title}</ItemTitle>}
                </StyledLink>

                {item.description && <ItemText>{item.description}</ItemText>}
              </Item>
            ))}
        </ItemList>

        {hasNextPage(page.current) && Boolean(items?.length) && (
          <LoadMoreButtonWrapper>
            {pageLoading ? (
              <LoadingIndicator />
            ) : (
              <LoadMoreButton onClick={loadNextPage}>Load more</LoadMoreButton>
            )}
          </LoadMoreButtonWrapper>
        )}
      </Wrapper>
    );
  },
);
